import { detectLocale, logCopyFallbacks, messages, onCopyError } from "@platform/ui";
import { TextProvider, type Translate } from "@platform/ui/features";
import type { ReactNode } from "react";
import { IntlProvider, useTranslations } from "use-intl";

export const locale = detectLocale(navigator.languages);
logCopyFallbacks(locale);

function SharedText({ children }: { children: ReactNode }) {
  const translations = useTranslations();
  const t: Translate = (key, values) => translations(key as never, values as never);
  return (
    <TextProvider t={t} locale={locale}>
      {children}
    </TextProvider>
  );
}

/** use-intl for the console's own pages and the same translator for the shared screens. */
export function I18nProvider({ children }: { children: ReactNode }) {
  return (
    <IntlProvider
      locale={locale}
      messages={messages[locale]}
      timeZone="Asia/Singapore"
      onError={(error) => onCopyError(error)}
    >
      <SharedText>{children}</SharedText>
    </IntlProvider>
  );
}

const dateTime = new Intl.DateTimeFormat(locale === "zh" ? "zh-CN" : "en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Asia/Singapore",
});
const relative = new Intl.RelativeTimeFormat(locale === "zh" ? "zh-CN" : "en", { numeric: "auto" });

export function formatDateTime(iso: string): string {
  return dateTime.format(new Date(iso));
}

/** How long something has waited, such as "3 hours ago" (OPS-1: the queue shows each case's age). */
export function formatAge(iso: string, now = Date.now()): string {
  const minutes = Math.round((new Date(iso).getTime() - now) / 60_000);
  if (Math.abs(minutes) < 60) return relative.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return relative.format(hours, "hour");
  return relative.format(Math.round(hours / 24), "day");
}
